import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/core/api/client";

export function useExportMyData() {
  return useMutation({
    mutationFn: () => api<Record<string, unknown>>("/api/account/export/"),
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: () => api<void>("/api/account/", { method: "DELETE" }),
  });
}

/** Hands the export to the browser as a file. Downloads can fail in a sandbox; caller catches. */
export function downloadJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** E-mail preferences. Opt-in: `decided` stays false until the user has answered once. */
export interface NotificationPreference {
  on_publication: boolean;
  weekly_digest: boolean;
  on_duel_turn: boolean;
  decided: boolean;
  updated_at: string | null;
}

export type NotificationChoice = Pick<
  NotificationPreference,
  "on_publication" | "weekly_digest" | "on_duel_turn"
>;

export const notificationPreferenceKey = ["account", "notifications"] as const;

export function useNotificationPreference(enabled = true) {
  return useQuery({
    queryKey: notificationPreferenceKey,
    queryFn: () => api<NotificationPreference>("/api/account/notifications/"),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateNotificationPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<NotificationChoice>) =>
      api<NotificationPreference>("/api/account/notifications/", { method: "PUT", json: body }),
    onSuccess: (data) => qc.setQueryData(notificationPreferenceKey, data),
  });
}
